import { SessionTracker } from './SessionTracker';
import type {
  CommandPayloadMap,
  ResponseType,
  StoresDispatcher,
} from '@api-client/wssWorker/StoresDispatcher';

type TrackingDispatcher = {
  postMessage: (
    message: { method: string; apiState?: { events?: any[] } }
  ) => Promise<ResponseType<CommandPayloadMap['trackSession']>>;
};

type TransportEntry = {
  trackingUserId: string;
  socketSessionId?: string;
  dispatcher: TrackingDispatcher;
};

export class TrackingService {
  private tracker = new SessionTracker({ enabled: true, batchSize: 10, flushInterval: 5000 });
  private transports = new Map<string, TransportEntry>();
  private activeTuneId: string | null = null;

  async ensureStarted(
    tuneId: string,
    trackingUserId: string,
    socketSessionId: string | undefined,
    storesDispatcher: StoresDispatcher
  ): Promise<void> {
    const dispatcher: TrackingDispatcher = {
      postMessage: async (message) =>
        storesDispatcher.sendTrackingEvents(tuneId, message.apiState?.events || []),
    };

    this.transports.set(tuneId, { trackingUserId, socketSessionId, dispatcher });

    if (!this.tracker.isActive()) {
      this.activeTuneId = tuneId;
      await this.tracker.start(trackingUserId, socketSessionId, dispatcher);
      return;
    }

    this.activeTuneId = tuneId;
    this.tracker.updateTransport(trackingUserId, socketSessionId, dispatcher);
  }

  async flushPending(tuneId?: string): Promise<void> {
    if (!this.tracker.isActive() || this.transports.size === 0) {
      return;
    }

    const targetTuneId = tuneId && this.transports.has(tuneId)
      ? tuneId
      : this.activeTuneId;

    if (targetTuneId && this.transports.has(targetTuneId)) {
      const transport = this.transports.get(targetTuneId) as TransportEntry;
      this.activeTuneId = targetTuneId;
      this.tracker.updateTransport(
        transport.trackingUserId,
        transport.socketSessionId,
        transport.dispatcher
      );
    }

    await this.tracker.flush();
  }

  release(tuneId: string): void {
    this.transports.delete(tuneId);

    if (this.transports.size === 0) {
      this.activeTuneId = null;
      this.tracker.stop();
      return;
    }

    if (this.activeTuneId === tuneId || this.activeTuneId === null) {
      const next = this.transports.entries().next().value as [string, TransportEntry];
      this.activeTuneId = next[0];
      this.tracker.updateTransport(next[1].trackingUserId, next[1].socketSessionId, next[1].dispatcher);
    }
  }
}

const trackingService = new TrackingService();

export default trackingService;
